import React, { useEffect, useState } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Input,
    FormGroup,
    Label,
    Button,
    Spinner,
    Alert
} from "reactstrap";
import { EditorState, convertToRaw, ContentState, convertFromHTML } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import getEpisodeById from "common/realBackend/blog/getEpisodeById";
import { extractDifference } from "helpers/extract-differemce";
import updateEpisode from "common/realBackend/blog/updateEpisode";

const EpisodesUpdate = () => {
    const { episodeId } = useParams();
    document.title = "Create Episode | AAOOA Admin";

    const [existingEpisode, setExistingEpisode] = useState(null)
    const [coverImagePreview, setCoverImagePreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [alertVisible, setAlertVisible] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [storyTextEditorState, setStoryTextEditorState] = useState(EditorState.createEmpty());

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Story Title is required"),
        duration: Yup.number().required("Duration is required"),
        coverImage: Yup.mixed().nullable(),
        audioLink: Yup.mixed().nullable(),
        videoLinkString: Yup.string().nullable()
    });

    const handleEditorChange = (state, setFieldValue, fieldName) => {
        const htmlContent = draftToHtml(convertToRaw(state.getCurrentContent()));

        setFieldValue(fieldName, htmlContent);
        setStoryTextEditorState(state);

    };

    useEffect(() => {
        getEpisodeById(episodeId)
            .then(({ data }) => {
                console.log(data)
                setExistingEpisode(data)
                setCoverImagePreview(data.image)
                const contentBlock = convertFromHTML(data.content);
                if (contentBlock) {
                    const contentState = ContentState.createFromBlockArray(
                        contentBlock.contentBlocks
                    );
                    const newEditorState = EditorState.createWithContent(contentState);
                    setStoryTextEditorState(newEditorState);
                }
            })
            .catch(err => {
                console.error(err)
                setAlertMessage("Error fetching episode. Please try again.");
                setAlertVisible(true);
            })
    }, [])

    const handleSubmit = async (values, { resetForm }) => {

        const diff = extractDifference(existingEpisode, values);
        if (!Object.keys(diff).length) {
            setAlertMessage("No change detected");
            setAlertVisible(true);
            return;
        }
        
        setLoading(true);
        try {
            await updateEpisode({id : episodeId, ...diff});
            setAlertMessage("Episode update successfully!");

            setAlertVisible(true);
        } catch (error) {
            console.error("Error creating:", error);
            setAlertMessage("Error updating. Please try again.");
            setAlertVisible(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="Stories" breadcrumbItem={"Update Episode"} />

                {alertVisible && (
                    <Alert color={alertMessage.includes("Error") ? "danger" : "success"}
                        toggle={() => setAlertVisible(false)}
                        style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 1050 }}>
                        {alertMessage}
                    </Alert>
                )}

                <Row>
                    <Col lg="12">
                        <Card>
                            <CardBody>
                                <CardTitle className="mb-4">{"Update Episode"}</CardTitle>
                                {
                                    existingEpisode ?
                                        <Formik
                                            initialValues={{
                                                name: existingEpisode.name,
                                                coverImage: null,
                                                duration: existingEpisode.duration,
                                                video: existingEpisode.video
                                            }}
                                            validationSchema={validationSchema}
                                            onSubmit={handleSubmit}
                                        >
                                            {({ setFieldValue }) => (
                                                <Form>
                                                    <FormGroup className="mb-4">
                                                        <Label htmlFor="name" className="col-form-label">Episode Title</Label>
                                                        <Field
                                                            id="name"
                                                            name="name"
                                                            as={Input}
                                                            placeholder="Enter Episode Title..."
                                                        />
                                                        <ErrorMessage name="name" component="div" className="text-danger" />
                                                    </FormGroup>

                                                    <FormGroup className="mb-4">
                                                        <Label htmlFor="duration" className="col-form-label">Episode Duration</Label>
                                                        <Field
                                                            id="duration"
                                                            name="duration"
                                                            as={Input}
                                                            placeholder="Enter Episode Duration..."
                                                        />
                                                        <ErrorMessage name="duration" component="div" className="text-danger" />
                                                    </FormGroup>


                                                    <FormGroup className="mb-4">
                                                        <Label className="col-form-label">Content</Label>
                                                        <Editor
                                                            editorState={storyTextEditorState}
                                                            toolbarClassName="toolbarClassName"
                                                            wrapperClassName="wrapperClassName"
                                                            editorClassName="editorClassName"
                                                            placeholder="Write the main story content here..."
                                                            onEditorStateChange={(state) => handleEditorChange(state, setFieldValue, "content")}
                                                        />
                                                        <ErrorMessage name="content" component="div" className="text-danger" />
                                                    </FormGroup>

                                                    <FormGroup className="mb-4">
                                                        <Label className="col-form-label">Cover Image</Label>
                                                        <Input
                                                            type="file"
                                                            className="form-control"
                                                            onChange={(e) => {
                                                                const file = e.currentTarget.files[0];
                                                                setFieldValue("image", file);
                                                                setCoverImagePreview(URL.createObjectURL(file));
                                                            }}
                                                        />
                                                        {coverImagePreview && (
                                                            <div className="mt-2">
                                                                <img src={coverImagePreview} alt="Cover Preview" style={{ maxWidth: '250px', height: 'auto' }} />
                                                            </div>
                                                        )}
                                                        <ErrorMessage name="coverImage" component="div" className="text-danger" />
                                                    </FormGroup>

                                                    <FormGroup className="mb-4">
                                                        <Label className="col-form-label">Upload Audio</Label>
                                                        <Input
                                                            type="file"
                                                            className="form-control"
                                                            onChange={(e) => setFieldValue("audio", e.currentTarget.files[0])}
                                                        />
                                                        {!!existingEpisode.audio && (
                                                            <div className="mt-2">
                                                                <audio controls>
                                                                    <source src={existingEpisode.audio} type="audio/mpeg" />
                                                                </audio>
                                                            </div>
                                                        )}
                                                    </FormGroup>

                                                    <FormGroup className="mb-4">
                                                        <Label htmlFor="video" className="col-form-label">Video Link</Label>
                                                        <Field
                                                            id="video"
                                                            name="video"
                                                            as={Input}
                                                            placeholder="Enter Video Link..."
                                                        />
                                                    </FormGroup>

                                                    <Row className="justify-content-end">
                                                        <Col lg="10">
                                                            <Button type="submit" color="primary" disabled={loading}>
                                                                {loading ? <Spinner size="sm" /> : "Update Episode"}
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </Form>
                                            )}
                                        </Formik> :
                                        (
                                            <Spinner color="primary" />
                                        )
                                }
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default EpisodesUpdate;
